import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import CTAButton from "../components/CTAButton";
import Partners from "../components/Partners";
import { Reveal } from "../components/Reveal";
import { FinalCTA } from "./Home";
import { getServices, getProjects } from "../lib/api";

const Pathway = () => (
  <div className="border border-ink/10 bg-white p-8 sm:p-10 mb-20" data-testid="service-pathway">
    <p className="label-tech text-graphite mb-6">Integrated service pathway</p>
    <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
      {["Design", "Coordinate", "Source", "Build", "Deliver"].map((step, i) => (
        <span key={step} className="flex items-center gap-4">
          <span className="t-h3 text-coal">{step}</span>
          {i < 4 && <ArrowRight size={20} className="text-cobalt" />}
        </span>
      ))}
    </div>
  </div>
);

const ServiceSection = ({ service, i, related }) => (
  <section id={service.slug} data-testid={`expertise-${service.slug}`} className="scroll-mt-24 border-t border-ink/10 py-16 sm:py-24">
    <div className="grid grid-cols-12 gap-8">
      <div className="col-span-12 lg:col-span-5">
        <Reveal>
          <span className="font-serif italic text-5xl sm:text-6xl text-ink/15">{service.number}</span>
          <h2 className="t-h2 text-coal mt-4">{service.title}</h2>
          <p className="font-serif italic text-xl sm:text-2xl text-cobalt mt-4">{service.tagline}</p>
          <p className="text-graphite text-[15px] leading-relaxed mt-6 max-w-md">{service.description}</p>
        </Reveal>
        <Reveal i={1} className="mt-8">
          <p className="label-tech text-graphite mb-3">Process phases</p>
          <div className="flex flex-wrap gap-2">
            {service.phases.map((p) => (
              <span key={p} className="label-tech border border-ink/15 px-3 py-1.5 text-coal">{p}</span>
            ))}
          </div>
        </Reveal>
      </div>
      <div className="col-span-12 lg:col-span-4">
        <Reveal i={1}>
          <p className="label-tech text-graphite mb-5">Capabilities</p>
          <ul className="space-y-0">
            {service.capabilities.map((c) => (
              <li key={c} className="py-3.5 border-t border-ink/10 text-[15px] text-coal flex items-baseline gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-cobalt shrink-0 translate-y-[-2px]" />
                {c}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
      <div className="col-span-12 lg:col-span-3">
        <Reveal i={2} className="overflow-hidden mb-6">
          <img src={service.image} alt={`${service.title} — reference image (placeholder)`} loading="lazy" className="w-full aspect-[4/5] object-cover hover:scale-[1.04] transition-transform duration-700 ease-expo" />
        </Reveal>
        {related.length > 0 && (
          <Reveal i={3}>
            <p className="label-tech text-graphite mb-3">Related projects</p>
            <ul className="space-y-2">
              {related.slice(0, 2).map((p) => (
                <li key={p.slug}>
                  <Link to={`/projects/${p.slug}`} data-testid={`related-${service.slug}-${p.slug}`} className="group inline-flex items-center gap-2 text-sm text-coal hover:text-cobalt transition-colors">
                    {p.title} <ArrowUpRight size={13} className="opacity-40 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </Reveal>
        )}
      </div>
    </div>
  </section>
);

const Expertise = () => {
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    getServices().then(setServices).catch(() => {});
    getProjects().then(setProjects).catch(() => {});
  }, []);

  return (
    <>
      <SEO title="Expertise | ARCH EXPO" description="Interior design, renovation and adaptation of existing spaces — supported by architecture, construction, procurement and project management." />
      <PageHero
        label="Expertise"
        titleLines={["Interiors, renovation", "& adaptation."]}
        intro="Design is at the core of our work — interior concepts, renovation and adaptation of existing spaces, supported by architecture, construction, procurement and project management as one coordinated service."
      >
        <Reveal i={3} className="mt-12 flex flex-wrap gap-3">
          {services.map((s) => (
            <a key={s.slug} href={`#${s.slug}`} data-testid={`anchor-${s.slug}`} className="label-tech border border-ink/15 px-4 py-2.5 text-graphite hover:text-coal hover:border-coal transition-colors min-h-[44px] inline-flex items-center">
              {s.number} — {s.title}
            </a>
          ))}
        </Reveal>
      </PageHero>
      <section className="bg-paper pb-24">
        <div className="container-ax">
          <Pathway />
          {services.map((s, i) => (
            <ServiceSection key={s.slug} service={s} i={i} related={projects.filter((p) => p.scope.includes(s.title))} />
          ))}
          <div className="pt-10">
            <CTAButton to="/contact" variant="dark" testId="expertise-cta">Discuss Your Project</CTAButton>
          </div>
        </div>
      </section>
      <Partners />
      <FinalCTA />
    </>
  );
};

export default Expertise;
